import { Mail, Phone, Briefcase, Trash2, IndianRupee, Pencil, FileSignature } from 'lucide-react';
import { EMP_TYPES, StatusPill, inr } from './_shared';

/**
 * One employee in the directory grid. Click anywhere except a button to navigate
 * to the employee detail page (handled by parent via `onOpen`).
 *
 * Buttons are conditionally rendered based on `canManage` / `isAdmin`.
 */
function EmployeeCard({ u, canManage, isAdmin, currentUserId, resigning, onOpen, onEdit, onOffer, onDelete }) {
  return (
    <div data-testid={`employee-card-${u.id}`}
         onClick={(e) => { if (e.target.closest('button')) return; onOpen(u.id); }}
         className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-[#3D4A8E] text-white flex items-center justify-center font-heading text-lg font-bold flex-shrink-0">
            {u.name?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 truncate">{u.name}</div>
            <div className="text-xs text-slate-500 truncate">{u.title || '—'}</div>
            {u.employee_id && <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mt-0.5">{u.employee_id}</div>}
          </div>
        </div>
        <div className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border whitespace-nowrap ${
          u.role === 'admin' ? 'bg-amber-50 text-amber-700 border-amber-200' :
          u.role === 'hr' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
          u.role === 'reporting_manager' ? 'bg-blue-50 text-blue-700 border-blue-200' :
          'bg-slate-50 text-slate-600 border-slate-200'
        }`}>{u.role === 'reporting_manager' ? 'RM' : u.role}</div>
      </div>
      <div className="mt-4 space-y-1.5 text-sm text-slate-600">
        <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" />{u.email}</div>
        {u.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-400" />{u.phone}</div>}
        <div className="flex items-center gap-2"><Briefcase className="w-3.5 h-3.5 text-slate-400" />{u.department || 'Unassigned'}{u.grade ? ` · ${u.grade}` : ''}</div>
        {u.annual_ctc != null && canManage && (
          <div className="flex items-center gap-2"><IndianRupee className="w-3.5 h-3.5 text-slate-400" />{inr(u.annual_ctc)} CTC</div>
        )}
      </div>
      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-1.5 flex-wrap">
          {u.terminated
            ? <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">Terminated</span>
            : <StatusPill status={u.onboarding_status} />}
          {!u.terminated && resigning && (
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">Resigning</span>
          )}
          {u.employment_type && u.employment_type !== 'full_time' && (
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border bg-slate-50 text-slate-600 border-slate-200">
              {EMP_TYPES.find(t => t.v === u.employment_type)?.label || u.employment_type}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <>
              <button onClick={() => onEdit(u)} data-testid={`edit-employee-${u.id}`}
                      className="text-xs text-slate-500 hover:text-[#3D4A8E] flex items-center gap-1">
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
              {u.annual_ctc != null && (
                <button onClick={() => onOffer(u)} data-testid={`offer-letter-${u.id}`}
                        className="text-xs text-slate-500 hover:text-[#3D4A8E] flex items-center gap-1">
                  <FileSignature className="w-3.5 h-3.5" /> Offer
                </button>
              )}
            </>
          )}
          {isAdmin && currentUserId !== u.id && (
            <button onClick={() => onDelete(u.id, u.name)} data-testid={`delete-employee-${u.id}`}
                    className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function EmployeesGrid({ items, canManage, isAdmin, currentUserId, resigningIds, onOpen, onEdit, onOffer, onDelete }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      {items.map(u => (
        <EmployeeCard key={u.id} u={u}
                      canManage={canManage} isAdmin={isAdmin} currentUserId={currentUserId}
                      resigning={resigningIds.has(u.id)}
                      onOpen={onOpen} onEdit={onEdit} onOffer={onOffer} onDelete={onDelete} />
      ))}
      {items.length === 0 && (
        <div className="col-span-full text-center py-16 text-slate-400">No employees found.</div>
      )}
    </div>
  );
}
